import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, PlayCircle, Check, Loader2, X, Pencil, Clock, Layers, Repeat, ChevronDown, Timer, Download,
  ThumbsUp, Dumbbell, HeartPulse, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { TonnageBar } from './TonnageBar';
import { ProgramCoherenceScore } from './ProgramCoherenceScore';

const SET_TYPE_LABELS = {
  straight: 'Séries droites', superset: 'Superset', biset: 'Bi-set', triset: 'Tri-set',
  giant_set: 'Série géante', pyramid_up: 'Pyramidal montant', pyramid_down: 'Pyramidal descendant',
  drop_set: 'Drop Set', rest_pause: 'Rest-Pause', cluster_set: 'Cluster Set',
  pre_exhaust: 'Pré-épuisement', post_exhaust: 'Post-épuisement', twenty_ones: '21s',
  amrap: 'AMRAP', emom: 'EMOM', tempo: 'Tempo', myo_reps: 'Myo-reps',
  circuit: 'Circuit', isometric: 'Isométrique', forced_reps: 'Reps forcées',
};

const OccurrenceGrid = ({ session, onViewLog, en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const total = session.sessions_target;
  const done = session.done_count;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`occurrence-grid-${session.session_label}`}>
      {Array.from({ length: total }).map((_, i) => {
        const num = i + 1;
        const isDone = num <= done;
        const isNext = num === done + 1;
        return (
          <button
            key={i}
            onClick={() => isDone && onViewLog(session, num)}
            disabled={!isDone}
            title={isDone ? `${L('Séance', 'Session')} ${num} — ${L('terminée', 'done')}` : isNext ? L('Prochaine séance', 'Next session') : L('À venir', 'Upcoming')}
            className={`w-7 h-7 rounded-lg text-[11px] font-bold font-mono flex items-center justify-center transition-all ${
              isDone ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/30 cursor-pointer'
              : isNext ? 'bg-primary/20 text-primary border-2 border-primary animate-pulse'
              : 'bg-white/5 text-zinc-600 border border-white/10'
            }`}
            data-testid={`occurrence-${session.session_label}-${num}`}
          >
            {isDone ? <Check className="w-3.5 h-3.5" /> : num}
          </button>
        );
      })}
    </div>
  );
};

const ExercisePreviewRow = ({ ex, en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const totalReviews = (ex.likes_count || 0) + (ex.dislikes_count || 0);
  const positivePct = totalReviews > 0 ? Math.round((ex.likes_count || 0) / totalReviews * 100) : null;
  return (
    <div className="py-2.5 border-b border-white/5 last:border-0 flex items-start gap-3" data-testid={`exercise-preview-${ex.exercise_id}`}>
      {ex.image_url && (
        <img src={`${process.env.REACT_APP_BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy"
          className="w-12 h-12 rounded-lg object-contain border border-white/10 bg-zinc-900 flex-shrink-0"
          data-testid={`exercise-preview-image-${ex.exercise_id}`} />
      )}
      <div className="flex-1 min-w-0">
      <p className="text-white text-sm font-semibold leading-snug" data-testid={`exercise-preview-name-${ex.exercise_id}`}>{ex.name}</p>
      <div className="flex items-center justify-between gap-3 mt-1 flex-wrap">
        <span className="text-[10px] text-primary font-bold shrink-0">{SET_TYPE_LABELS[ex.set_type] || ex.set_type}</span>
        <div className="flex items-center gap-3 text-[11px] text-zinc-400 font-mono shrink-0">
          <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {ex.sets_count}</span>
          <span className="inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> {ex.reps_scheme}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ex.rest_seconds}s</span>
          {ex.tempo && <span className="inline-flex items-center gap-1" data-testid={`exercise-preview-tempo-${ex.exercise_id}`}><Timer className="w-3 h-3" /> {ex.tempo}</span>}
          {ex.tonnage_target != null && <span className="text-purple-300">🎯 {Math.round(ex.tonnage_target)}kg</span>}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-1 flex-wrap">
        {positivePct != null && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400" data-testid={`exercise-preview-reviews-${ex.exercise_id}`}>
            <ThumbsUp className="w-3 h-3" /> {positivePct}% ({totalReviews})
          </span>
        )}
        {ex.type && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-400" data-testid={`exercise-preview-type-${ex.exercise_id}`}>
            {ex.type === 'cardio' ? <HeartPulse className="w-3 h-3" /> : <Dumbbell className="w-3 h-3" />}
            {ex.type === 'cardio' ? L('Cardio', 'Cardio') : L('Musculation', 'Strength')}
          </span>
        )}
      </div>
      {ex.notes && <p className="text-[11px] text-zinc-400 mt-1 italic">{ex.notes}</p>}
      </div>
    </div>
  );
};

/** Vue d'ensemble d'un programme : aperçu complet des séances/exercices + organigramme des occurrences. */
export const ProgramOverview = ({ program, en, onBack, onStartSession, onEditLog, authFetch }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [expanded, setExpanded] = useState({});
  const [logModal, setLogModal] = useState(null); // { session, log, loading }
  const [pdfBusy, setPdfBusy] = useState(false);

  const toggle = (label) => setExpanded((p) => ({ ...p, [label]: !p[label] }));

  const weekNumber = program.created_at
    ? Math.max(1, Math.floor((Date.now() - new Date(program.created_at).getTime()) / (7 * 24 * 3600 * 1000)) + 1)
    : 1;

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await authFetch(`/api/tracking/programs-pro/${program.program_id}/pdf`);
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `programme-${program.name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(L('Échec du téléchargement du PDF.', 'PDF download failed.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const viewLog = async (session, occurrenceNumber) => {
    setLogModal({ session, occurrenceNumber, log: null, loading: true });
    try {
      const res = await authFetch(`/api/tracking/programs-pro/${program.program_id}/session/${encodeURIComponent(session.session_label)}/logs`);
      const data = await res.json();
      const log = (data.logs || []).find((l) => l.occurrence_number === occurrenceNumber);
      setLogModal({ session, occurrenceNumber, log, loading: false });
    } catch {
      setLogModal(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="program-detail-view">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="back-to-programs-list-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Mes programmes', 'My programs')}
      </button>
      <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-pink-500/10 border border-primary/20 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-black text-white">{program.name}</h2>
            <p className="text-sm text-zinc-400 mt-1">{program.goal} · {L('Objectif', 'Target')} : {program.weeks_target} {L('semaines', 'weeks')} · +{program.tonnage_growth_target_pct}% {L('de tonnage visé', 'targeted tonnage growth')}</p>
          </div>
          <button onClick={downloadPdf} disabled={pdfBusy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-50" data-testid="download-program-pdf-btn">
            {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {L('Télécharger le PDF', 'Download PDF')}
          </button>
        </div>
      </div>

      <ProgramCoherenceScore sessions={program.sessions} en={en} />

      {weekNumber >= 2 && (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3.5 flex items-start gap-2.5" data-testid="week-progression-banner">
          <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-200">
            {L(
              `Vous êtes en semaine ${weekNumber} de ce programme : essayez d'ajouter +1 répétition ou +2,5% de charge sur vos exercices par rapport à la dernière séance, pour continuer à progresser.`,
              `You're in week ${weekNumber} of this program: try adding +1 rep or +2.5% weight on your exercises vs your last session to keep progressing.`
            )}
          </p>
        </div>
      )}

      {program.sessions.map((s) => (
        <div key={s.session_label} className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid={`session-card-${s.session_label}`}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="text-white font-bold">{s.session_label}</h3>
            <span className="text-xs text-zinc-500 font-mono">{s.done_count}/{s.sessions_target} {L('séances', 'sessions')}</span>
          </div>

          <OccurrenceGrid session={s} onViewLog={viewLog} en={en} />

          {s.last_tonnage != null && (
            <div className="mt-3">
              <TonnageBar current={s.last_tonnage} target={s.tonnage_target} label={L('Dernier tonnage vs objectif prochain', 'Last tonnage vs next target')} />
            </div>
          )}

          <button onClick={() => toggle(s.session_label)} className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-400 hover:text-white" data-testid={`toggle-preview-${s.session_label}`}>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded[s.session_label] ? 'rotate-180' : ''}`} /> {L('Aperçu des exercices', 'Exercise preview')}
          </button>
          <AnimatePresence>
            {expanded[s.session_label] && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="mt-2 rounded-xl bg-black/30 px-3 pt-1">
                  {s.exercises.map((ex) => <ExercisePreviewRow key={ex.exercise_id} ex={ex} en={en} />)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button onClick={() => onStartSession(s)} className="w-full mt-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-pink-500 text-white font-bold text-sm inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform" data-testid={`start-session-btn-${s.session_label}`}>
            <PlayCircle className="w-4 h-4" /> {L('Démarrer la séance', 'Start session')} {s.done_count + 1}
          </button>
        </div>
      ))}

      <AnimatePresence>
        {logModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLogModal(null)} data-testid="log-detail-modal">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-white flex items-center gap-2 flex-wrap">
                  {logModal.session.session_label} — {L('Séance', 'Session')} {logModal.occurrenceNumber}
                  {logModal.log?.edited_at && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-sky-500/15 text-sky-300 border border-sky-500/30" data-testid="log-edited-badge">
                      <Pencil className="w-2.5 h-2.5" /> {L('Modifié', 'Edited')}
                    </span>
                  )}
                </h4>
                <button onClick={() => setLogModal(null)} className="text-zinc-500 hover:text-white" data-testid="close-log-modal-btn"><X className="w-5 h-5" /></button>
              </div>
              {logModal.loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : logModal.log ? (
                <>
                  <p className="text-2xl font-black text-primary mb-3">{logModal.log.session_tonnage} kg</p>
                  <div className="space-y-2 mb-4">
                    {logModal.log.exercises.map((ex) => (
                      <div key={ex.exercise_id} className="text-sm">
                        <p className="text-white font-semibold">{ex.name} <span className="text-zinc-500 font-normal">— {ex.tonnage}kg</span></p>
                        <p className="text-zinc-500 text-xs">{ex.sets.map((s) => `${s.reps}×${s.weight}kg`).join(' · ')}</p>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => { onEditLog(logModal.session, logModal.log); setLogModal(null); }} className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors" data-testid="edit-log-btn">
                    <Pencil className="w-3.5 h-3.5" /> {L('Corriger cette séance', 'Fix this session')}
                  </button>
                </>
              ) : (
                <p className="text-zinc-500 text-sm text-center py-6">{L('Séance introuvable.', 'Session not found.')}</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProgramOverview;
